import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { LayoutGrid, List, SlidersHorizontal, X } from "lucide-react";
import { API, logErr, inr } from "../lib/api";
import ProductCard from "../components/ProductCard";

const SIZES = ["S", "M", "L", "XL", "XXL"];
const CATS = [
  { key: "", label: "All" },
  { key: "streetwear", label: "Streetwear" },
  { key: "casual", label: "Casual Wear" },
];
const SORTS = [
  { key: "newest", label: "Newest" },
  { key: "popular", label: "Popularity" },
  { key: "price_asc", label: "Price: Low to High" },
  { key: "price_desc", label: "Price: High to Low" },
];

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState({ products: [], total: 0, pages: 1 });
  const [view, setView] = useState("grid");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [colors, setColors] = useState([]);
  const [loading, setLoading] = useState(true);

  const category = params.get("category") || "";
  const q = params.get("q") || "";
  const sort = params.get("sort") || "newest";
  const size = params.get("size") || "";
  const color = params.get("color") || "";
  const minP = params.get("min") || "";
  const maxP = params.get("max") || "";
  const page = parseInt(params.get("page") || "1");

  const setParam = (k, v) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v); else next.delete(k);
    if (k !== "page") next.delete("page");
    setParams(next);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = { sort, page, limit: 12 };
      if (category) p.category = category;
      if (q) p.q = q;
      if (size) p.size = size;
      if (color) p.color = color;
      if (minP) p.min_price = minP;
      if (maxP) p.max_price = maxP;
      const r = await API.get("/products", { params: p });
      setData(r.data);
    } catch (err) {
      logErr("Failed to load products:", err);
    } finally { setLoading(false); }
  }, [category, q, sort, size, color, minP, maxP, page, API, logErr]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    API.get("/products", { params: { limit: 100 } }).then((r) => {
      const c = new Set();
      r.data.products.forEach((p) => p.colors.forEach((x) => c.add(x)));
      setColors([...c]);
    }).catch((err) => logErr("Failed to load color filters:", err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    document.title = `${category ? CATS.find(c => c.key === category)?.label + " — " : q ? `Search: ${q} — ` : ""}Shop | Dressedin by Urbanz`;
  }, [category, q]);

  const Filters = () => (
    <div className="space-y-8" data-testid="filters-panel">
      <div>
        <h4 className="text-xs uppercase tracking-[0.25em] text-gold font-bold mb-3">Category</h4>
        <div className="space-y-2">
          {CATS.map((c) => (
            <button key={c.key} onClick={() => setParam("category", c.key)} data-testid={`filter-cat-${c.key || "all"}`}
              className={`block text-sm transition-colors ${category === c.key ? "text-gold font-bold" : "text-muted-foreground hover:text-foreground"}`}>
              {c.label}
            </button>
          ))}
        </div>
      </div>
      <div>
        <h4 className="text-xs uppercase tracking-[0.25em] text-gold font-bold mb-3">Size</h4>
        <div className="flex flex-wrap gap-2">
          {SIZES.map((s) => (
            <button key={s} onClick={() => setParam("size", size === s ? "" : s)} data-testid={`filter-size-${s}`}
              className={`px-3 py-1.5 text-xs border transition-colors ${size === s ? "border-gold bg-gold text-ink font-bold" : "border-[#262626] text-muted-foreground hover:border-gold/60"}`}>
              {s}
            </button>
          ))}
        </div>
      </div>
      <div>
        <h4 className="text-xs uppercase tracking-[0.25em] text-gold font-bold mb-3">Color</h4>
        <div className="flex flex-wrap gap-2">
          {colors.map((c) => (
            <button key={c} onClick={() => setParam("color", color === c ? "" : c)} data-testid={`filter-color-${c.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
              className={`px-3 py-1.5 text-xs border transition-colors ${color === c ? "border-gold text-gold font-bold" : "border-[#262626] text-muted-foreground hover:border-gold/60"}`}>
              {c}
            </button>
          ))}
        </div>
      </div>
      <div>
        <h4 className="text-xs uppercase tracking-[0.25em] text-gold font-bold mb-3">Price range</h4>
        <div className="flex items-center gap-2">
          <input data-testid="filter-min-price" type="number" placeholder="Min" value={minP} onChange={(e) => setParam("min", e.target.value)}
            className="w-full bg-ink border border-[#262626] px-3 py-2 text-sm outline-none focus:border-gold" />
          <span className="text-muted-foreground">—</span>
          <input data-testid="filter-max-price" type="number" placeholder="Max" value={maxP} onChange={(e) => setParam("max", e.target.value)}
            className="w-full bg-ink border border-[#262626] px-3 py-2 text-sm outline-none focus:border-gold" />
        </div>
      </div>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10" data-testid="shop-page">
      <div className="mb-10">
        <p className="text-xs uppercase tracking-[0.3em] text-gold font-bold mb-2">{q ? `Results for "${q}"` : "The catalog"}</p>
        <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter">
          {category ? CATS.find((c) => c.key === category)?.label : q ? "Search" : "All products"}
        </h1>
        <p className="text-sm text-muted-foreground mt-2">{data.total} pieces</p>
      </div>

      <div className="flex gap-10">
        <aside className="hidden lg:block w-56 shrink-0"><Filters /></aside>
        <div className="flex-1">
          <div className="flex items-center justify-between gap-4 mb-8 border-b border-[#262626] pb-4">
            <button onClick={() => setFiltersOpen(true)} className="lg:hidden flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground" data-testid="open-filters-btn">
              <SlidersHorizontal size={16} /> Filters
            </button>
            <select data-testid="sort-select" value={sort} onChange={(e) => setParam("sort", e.target.value)}
              className="bg-ink border border-[#262626] px-3 py-2 text-xs uppercase tracking-wider outline-none focus:border-gold ml-auto">
              {SORTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
            <div className="flex border border-[#262626]">
              <button onClick={() => setView("grid")} data-testid="view-grid-btn"
                className={`p-2 ${view === "grid" ? "bg-gold text-ink" : "text-muted-foreground"}`}><LayoutGrid size={16} /></button>
              <button onClick={() => setView("list")} data-testid="view-list-btn"
                className={`p-2 ${view === "list" ? "bg-gold text-ink" : "text-muted-foreground"}`}><List size={16} /></button>
            </div>
          </div>

          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
              {Array(6).fill(0).map((_, i) => <div key={`sk-${i}`} className="aspect-[3/4] bg-ink-surface animate-pulse" />)}
            </div>
          ) : data.products.length === 0 ? (
            <div className="text-center py-24" data-testid="no-products">
              <p className="font-display text-2xl font-bold">Nothing found</p>
              <p className="text-sm text-muted-foreground mt-2">Try clearing filters or a different search.</p>
            </div>
          ) : view === "grid" ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-5 md:gap-6" data-testid="product-grid">
              {data.products.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
            </div>
          ) : (
            <div className="space-y-4" data-testid="product-list">
              {data.products.map((p) => (
                <a key={p.id} href={`/product/${p.slug}`} data-testid={`product-row-${p.slug}`}
                  className="flex gap-5 border border-[#262626] bg-ink-surface p-4 hover:border-gold/50 transition-colors">
                  <img src={p.images[0]} alt={p.name} loading="lazy" className="h-28 w-24 object-cover" />
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{p.subcategory}</p>
                    <h3 className="font-display font-bold">{p.name}</h3>
                    <p className="text-xs text-muted-foreground line-clamp-2 mt-1">{p.description}</p>
                    <p className="text-gold font-bold mt-2">{inr(p.sale_price || p.price)}
                      {p.sale_price && <span className="text-muted-foreground text-xs line-through ml-2">{inr(p.price)}</span>}
                    </p>
                  </div>
                </a>
              ))}
            </div>
          )}

          {data.pages > 1 && (
            <div className="flex justify-center gap-2 mt-12" data-testid="pagination">
              {Array.from({ length: data.pages }, (_, i) => i + 1).map((n) => (
                <button key={n} onClick={() => setParam("page", String(n))} data-testid={`page-${n}`}
                  className={`h-10 w-10 text-sm border transition-colors ${page === n ? "bg-gold text-ink border-gold font-bold" : "border-[#262626] text-muted-foreground hover:border-gold/60"}`}>
                  {n}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {filtersOpen && (
        <div className="fixed inset-0 z-50 lg:hidden" data-testid="mobile-filters">
          <div className="absolute inset-0 bg-black/70" onClick={() => setFiltersOpen(false)} />
          <div className="absolute left-0 top-0 bottom-0 w-72 bg-ink border-r border-[#262626] p-6 overflow-y-auto">
            <div className="flex items-center justify-between mb-8">
              <h3 className="font-display font-bold">Filters</h3>
              <button onClick={() => setFiltersOpen(false)} data-testid="close-filters-btn"><X size={20} /></button>
            </div>
            <Filters />
            <button onClick={() => setFiltersOpen(false)} className="btn-gold w-full mt-8 py-3 text-xs font-bold uppercase tracking-widest" data-testid="apply-filters-btn">
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
